import React, { useEffect, useState, useCallback } from 'react';
import { Timer } from 'lucide-react';
import { api } from '../lib/api';

/* [PATCH pregled-dela v4]
   Superadmin -> Pregled dela. Bere GET /delo/porocilo (samo superadmin).

   Dve razlicni casovni osi, zato dve loceni tabeli:
   - "Povprecje na racun" tece po mesecu POTRDITVE dokumenta (approved_at);
   - "Cas po fazah" po mesecu DELA (zacetek odseka).
   Mesanje obeh v eni vrstici bi dalo stolpce, ki se ne sestejejo.

   Stevci dokumentov delujejo za nazaj (od 05/2026); cas se meri od
   zacetka meritev (zacetek_meritev v odgovoru). */

const PRAGOVI = [
  { v: 2, opis: '2 s (privzeto)' }, /* [PATCH pregled-dela v6b] */
  { v: 5, opis: '5 s' },
  { v: 15, opis: '15 s' },
  { v: 30, opis: '30 s' },
  { v: 60, opis: '60 s (≈ zidna ura)' },
];

const FAZE_VRSTNI_RED = ['nalaganje', 'pregled', 'popravki', 'prenos', 'splosno'];
/* [PATCH pregled-dela v5] 'popravki' je faza po potrditvi dokumenta — vsebuje tudi
   navaden rep med potrditvijo in klikom na naslednji dokument (mediana 6 s).
   Zato "Po potrditvi", ne "Popravki". Stolpec "S popravki" je nekaj drugega:
   dokumenti z >=2 potrditvama v dnevniku (resnicno vracanje). */
const FAZE_NAZIVI: Record<string, string> = {
  nalaganje: 'Nalaganje', pregled: 'Pregled', popravki: 'Po potrditvi',
  prenos: 'Prenos', splosno: 'Splošno',
};
const VRSTE_NAZIVI: Record<string, string> = {
  prejeti_racun: 'Prejeti računi', izdani_racun: 'Izdani računi',
};

function fmtMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return ms > 0 ? '<1 s' : '0';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = ms / 60000;
  if (m < 60) return `${m.toFixed(1).replace('.', ',')} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${Math.round(m % 60)} min`;
}

function fmtPct(del: number, celota: number): string {
  if (!celota) return '—';
  return `${((100 * del) / celota).toFixed(1).replace('.', ',')} %`;
}

function fmtMesec(m: string): string {
  // '2026-07-01' -> '07/2026'
  const [l, me] = m.split('-');
  return `${me}/${l}`;
}

const th = 'px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 whitespace-nowrap';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const tdNum = td + ' text-right tabular-nums';
const kartica = 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl';

export function SuperadminDeloPage() {
  const danes = new Date().toISOString().slice(0, 10);
  const [od, setOd] = useState('2026-05-01');
  const [doD, setDoD] = useState(danes);
  const [prag, setPrag] = useState(2); /* [PATCH pregled-dela v6b] privzeti prag 2 s */
  const [busy, setBusy] = useState(false);
  const [napaka, setNapaka] = useState('');
  const [data, setData] = useState<any>(null);

  const nalozi = useCallback(async () => {
    setBusy(true);
    setNapaka('');
    try {
      const r = await api.get(`/delo/porocilo?od=${od}&do=${doD}&prag=${prag}`);
      setData(r.data);
    } catch (e: any) {
      setNapaka(e?.response?.data?.detail || 'Poročila ni bilo mogoče naložiti');
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [od, doD, prag]);

  useEffect(() => { nalozi(); }, [nalozi]);

  // faze[] -> mapa (mesec|vrsta) -> {faza: ms}
  const fazeMapa: Record<string, Record<string, number>> = {};
  const fazeMeseci = new Set<string>();
  if (data?.faze) {
    for (const f of data.faze) {
      if (f.doc_type !== 'prejeti_racun' && f.doc_type !== 'izdani_racun') continue;
      const k = `${f.mesec}|${f.doc_type}`;
      (fazeMapa[k] ||= {})[f.faza] = f.ms;
      fazeMeseci.add(k);
    }
  }
  const fazeVrstice = Array.from(fazeMeseci).sort().map(k => {
    const [mesec, vrsta] = k.split('|');
    return { mesec, vrsta, po: fazeMapa[k] };
  });

  const pokritostPct = data?.pokritost?.vse_ms
    ? (100 * data.pokritost.zunaj_ms) / data.pokritost.vse_ms : 0;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-100 dark:bg-emerald-500/25 text-emerald-600 dark:text-emerald-300">
          <Timer className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pregled dela</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Aktivni čas in učinkovitost po računovodjah
          </p>
        </div>
      </div>

      {/* filtri */}
      <div className={kartica + ' p-4 flex flex-wrap items-end gap-4'}>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Od</span>
          <input type="date" value={od} onChange={e => setOd(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Do (vključno)</span>
          <input type="date" value={doD} onChange={e => setDoD(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Prag neaktivnosti</span>
          <select value={prag} onChange={e => setPrag(Number(e.target.value))}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm">
            {PRAGOVI.map(p => <option key={p.v} value={p.v}>{p.opis}</option>)}
          </select>
        </label>
        {busy && <span className="text-sm text-gray-400 pb-1.5">Nalagam …</span>}
      </div>

      {napaka && (
        <div className="rounded-xl border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {napaka}
        </div>
      )}

      {data && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Čas se meri od <b>{data.zacetek_meritev}</b>; števci dokumentov veljajo za nazaj.
            Pri pragu {prag} s se vrzel brez aktivnosti šteje največ {prag} s.
            {pokritostPct > 20 && (
              <span className="text-amber-600 dark:text-amber-400">
                {' '}Opozorilo: {pokritostPct.toFixed(0)} % dokumentnega časa pripada dokumentom
                zunaj izbranega obdobja — povprečja beri previdno.
              </span>
            )}
          </p>

          {/* 1 — trend: povprecje na racun (mesec POTRDITVE) */}
          <div className={kartica + ' overflow-hidden'}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">Povprečje na račun</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Po mesecu potrditve; povprečje = čas urejanja računa (faza Pregled) / N — brez nalaganja, prenosa in časa po potrditvi. Popravljeni / samo potrjeni: samo računi z meritvijo (od 31.10.2026), prikaz »število · povprečje«.{/* [PATCH pregled-dela v6] */}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className={th}>Mesec</th><th className={th}>Vrsta</th>
                    <th className={th + ' text-right'}>N</th>
                    <th className={th + ' text-right'}>Povprečje</th>
                    <th className={th + ' text-right'}>Mediana (dok.)</th>
                    <th className={th + ' text-right'}>Čas urejanja</th>
                    <th className={th + ' text-right'}>Popravljeni</th><th className={th + ' text-right'}>Samo potrjeni</th>{/* [PATCH pregled-dela v6] */}
                    <th className={th + ' text-right'}>S popravki</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.trend.length === 0 && (
                    <tr><td className={td} colSpan={9}>Ni potrjenih dokumentov v obdobju.</td></tr>
                  )}
                  {data.trend.map((t: any, i: number) => (
                    <tr key={i}>
                      <td className={td}>{fmtMesec(t.mesec)}</td>
                      <td className={td}>{VRSTE_NAZIVI[t.doc_type] || t.doc_type}</td>
                      <td className={tdNum}>{t.n}</td>
                      <td className={tdNum + ' font-semibold'}>{fmtMs(t.povp_ms)}</td>
                      <td className={tdNum}>{fmtMs(t.mediana_ms)}</td>
                      <td className={tdNum}>{fmtMs(t.dok_ms)}</td>
                      <td className={tdNum}>{t.n_izmerjeno ? `${t.n_popravljen} · ${fmtMs(t.povp_popravljen_ms)}` : '—'}</td><td className={tdNum}>{t.n_izmerjeno ? `${t.n_samo_potrjen} · ${fmtMs(t.povp_samo_ms)}` : '—'}</td>{/* [PATCH pregled-dela v6] */}
                      <td className={tdNum}>{t.n_s_popravki} ({fmtPct(t.n_s_popravki, t.n)})</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 2 — cas po fazah (mesec DELA) */}
          <div className={kartica + ' overflow-hidden'}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">Čas po fazah</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Po mesecu dela (od začetka meritev). »Po potrditvi« je čas na dokumentu po njegovi potrditvi — vključno s kratkim repom pred klikom na naslednji dokument.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className={th}>Mesec</th><th className={th}>Vrsta</th>
                    {FAZE_VRSTNI_RED.map(f => <th key={f} className={th + ' text-right'}>{FAZE_NAZIVI[f]}</th>)}
                    <th className={th + ' text-right'}>Skupaj</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {fazeVrstice.length === 0 && (
                    <tr><td className={td} colSpan={8}>Ni izmerjenega časa v obdobju.</td></tr>
                  )}
                  {fazeVrstice.map((v, i) => {
                    const skupaj = FAZE_VRSTNI_RED.reduce((a, f) => a + (v.po[f] || 0), 0);
                    return (
                      <tr key={i}>
                        <td className={td}>{fmtMesec(v.mesec)}</td>
                        <td className={td}>{VRSTE_NAZIVI[v.vrsta] || v.vrsta}</td>
                        {FAZE_VRSTNI_RED.map(f => (
                          <td key={f} className={tdNum}>{fmtMs(v.po[f] || 0)}</td>
                        ))}
                        <td className={tdNum + ' font-semibold'}>{fmtMs(skupaj)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* 3 — racunovodje */}
          <div className={kartica + ' overflow-hidden'}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">Po računovodjah</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Čas v programu po mesecu dela; števci in povprečja po mesecu potrditve.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className={th}>Mesec</th><th className={th}>Računovodja</th>
                    <th className={th + ' text-right'}>Čas v programu</th>
                    <th className={th + ' text-right'}>Prejeti</th>
                    <th className={th + ' text-right'}>Povp. prejeti</th>
                    <th className={th + ' text-right'}>Izdani</th>
                    <th className={th + ' text-right'}>Povp. izdani</th>
                    <th className={th + ' text-right'}>Drugo</th>
                    <th className={th + ' text-right'}>S popravki</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.racunovodje.length === 0 && (
                    <tr><td className={td} colSpan={9}>Ni podatkov v obdobju.</td></tr>
                  )}
                  {data.racunovodje.map((r: any, i: number) => {
                    const nRacunov = r.prejeti_n + r.izdani_n;
                    const nPop = r.prejeti_pop + r.izdani_pop;
                    return (
                      <tr key={i}>
                        <td className={td}>{fmtMesec(r.mesec)}</td>
                        <td className={td}>{r.ime}</td>
                        <td className={tdNum}>{r.cas_ms ? fmtMs(r.cas_ms) : '—'}</td>
                        <td className={tdNum}>{r.prejeti_n}</td>
                        <td className={tdNum}>{fmtMs(r.prejeti_povp_ms)}</td>
                        <td className={tdNum}>{r.izdani_n}</td>
                        <td className={tdNum}>{fmtMs(r.izdani_povp_ms)}</td>
                        <td className={tdNum}>{r.drugo_n}</td>
                        <td className={tdNum}>{nPop} ({fmtPct(nPop, nRacunov)})</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* 4 — podjetja */}
          <div className={kartica + ' overflow-hidden'}>
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-semibold text-gray-900 dark:text-white">Po podjetjih</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Potrjeni dokumenti v obdobju in izmerjeni čas na podjetju.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th className={th}>Podjetje</th><th className={th}>Računovodja</th>
                    <th className={th + ' text-right'}>Prejeti</th>
                    <th className={th + ' text-right'}>Izdani</th>
                    <th className={th + ' text-right'}>Drugo</th>
                    <th className={th + ' text-right'}>Čas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {data.podjetja.length === 0 && (
                    <tr><td className={td} colSpan={6}>Ni podatkov v obdobju.</td></tr>
                  )}
                  {data.podjetja.map((p: any, i: number) => (
                    <tr key={i}>
                      <td className={td + ' max-w-[26rem] truncate'} title={p.podjetje}>{p.podjetje}</td>
                      <td className={td}>{p.ime}</td>
                      <td className={tdNum}>{p.prejeti_n}</td>
                      <td className={tdNum}>{p.izdani_n}</td>
                      <td className={tdNum}>{p.drugo_n}</td>
                      <td className={tdNum}>{p.cas_ms ? fmtMs(p.cas_ms) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
